import React, { useEffect, useState } from 'react';
import { ACCENT, ACCENT_LIGHT, ACCENT_BORDER, TEXT_DARK, TEXT_MID, TEXT_LIGHT, FONT } from '../lib/tokens';
import { Ic } from '../components/Icons';
import { Card, ScreenWrap, SectionLabel } from '../components/ui/Primitives';
import { supabase, hasSupabase } from '../lib/supabase';
import { initials } from '../data/attendees';
import { downloadVCard } from '../lib/vcard';
import { useBreakpoint } from '../lib/useBreakpoint';

export default function AttendeesScreen() {
  const [search, setSearch] = useState('');
  const [downloaded, setDownloaded] = useState(null);
  // Start empty and load from the DB on mount. The service worker caches the
  // last successful fetch, so an offline cold-start still gets the most-recent
  // roster. No static fallback file to keep in sync.
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const bp = useBreakpoint();
  const isDesktop = bp === 'desktop';

  useEffect(() => {
    if (!hasSupabase) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    supabase
      .from('attendees')
      .select('*')
      .order('name', { ascending: true })
      .then(({ data }) => {
        if (cancelled) return;
        setAttendees(data || []);
        setLoading(false);
      });

    // Realtime: admin add/edit/delete propagates to every open screen without
    // a refresh. We just refetch on any change — the roster is small enough
    // that diffing payloads isn't worth it.
    const channel = supabase
      .channel('attendees-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'attendees' }, () => {
        supabase
          .from('attendees')
          .select('*')
          .order('name', { ascending: true })
          .then(({ data }) => {
            if (!cancelled) setAttendees(data || []);
          });
      })
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, []);

  // Drop hidden attendees before anything else — they shouldn't appear in
  // the list, the count, or any search result. Admin can flip the flag back
  // off whenever they want to restore visibility.
  const visible = attendees.filter((a) => a.hidden !== true);
  const filtered = visible.filter(
    (a) =>
      a.name.toLowerCase().includes(search.toLowerCase()) ||
      (a.organization || '').toLowerCase().includes(search.toLowerCase()),
  );

  const handleDownload = (a) => {
    downloadVCard(a);
    setDownloaded(a.id);
    setTimeout(() => setDownloaded(null), 2000);
  };

  const search_field = (
    <div style={{ position: 'relative', maxWidth: isDesktop ? 360 : '100%' }}>
      <div style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }}>
        <Ic.Search size={15} color="#94a3b8" />
      </div>
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search attendees…"
        style={{
          width: '100%',
          padding: '10px 11px 10px 33px',
          border: 'none',
          borderRadius: 10,
          background: 'rgba(255,255,255,0.07)',
          color: '#fff',
          fontSize: 13.5,
          fontFamily: FONT,
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    </div>
  );

  const renderCard = (a) => {
    const highlight = a.role === 'facilitator' || a.role === 'host';
    // Per-attendee privacy: when share_contact is explicitly false, hide the
    // phone link and the Download vCard button. Name + title + org still show.
    // Treat undefined as true so older rows behave as before.
    const sharesContact = a.share_contact !== false;
    return (
      <Card
        key={a.id}
        style={{
          display: 'flex',
          flexDirection: isDesktop ? 'column' : 'row',
          alignItems: isDesktop ? 'flex-start' : 'center',
          gap: isDesktop ? 10 : 12,
          padding: isDesktop ? '16px 16px 14px' : '11px 13px',
          border: highlight ? `1.5px solid ${ACCENT_BORDER}` : '1.5px solid transparent',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
          <div
            style={{
              width: isDesktop ? 44 : 40,
              height: isDesktop ? 44 : 40,
              borderRadius: 11,
              flexShrink: 0,
              background: highlight ? ACCENT : ACCENT_LIGHT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span
              style={{ fontSize: 14, fontWeight: 700, color: highlight ? '#fff' : ACCENT }}
            >
              {initials(a.name)}
            </span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: TEXT_DARK, lineHeight: 1.2 }}>
              {a.name}
            </div>
            <div style={{ fontSize: 11.5, color: TEXT_MID, marginTop: 1 }}>{a.title}</div>
          </div>
          {!isDesktop && sharesContact && (
            <DownloadBtn
              attendee={a}
              done={downloaded === a.id}
              onClick={() => handleDownload(a)}
            />
          )}
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: TEXT_LIGHT,
            marginTop: isDesktop ? 6 : 2,
            overflow: isDesktop ? 'visible' : 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: isDesktop ? 'normal' : 'nowrap',
            lineHeight: 1.35,
            paddingLeft: isDesktop ? 0 : 52,
          }}
        >
          {a.organization}
        </div>
        {sharesContact && a.phone && (
          <a
            href={`tel:${a.phone.replace(/[^\d+]/g, '')}`}
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              marginTop: isDesktop ? 4 : 2,
              paddingLeft: isDesktop ? 0 : 52,
              fontSize: 12,
              fontWeight: 600,
              color: ACCENT,
              textDecoration: 'none',
              fontFamily: FONT,
            }}
          >
            <Ic.Phone size={12} color={ACCENT} />
            {formatPhone(a.phone)}
          </a>
        )}
        {isDesktop && sharesContact && (
          <div style={{ marginTop: 'auto', paddingTop: 10, width: '100%' }}>
            <DownloadBtn
              wide
              attendee={a}
              done={downloaded === a.id}
              onClick={() => handleDownload(a)}
            />
          </div>
        )}
      </Card>
    );
  };

  const countLabel = loading
    ? 'LOADING…'
    : `${filtered.length} ${filtered.length === 1 ? 'ATTENDEE' : 'ATTENDEES'}`;

  const emptyMessage =
    loading
      ? null
      : visible.length === 0
      ? 'No attendees yet.'
      : filtered.length === 0
      ? 'No attendees match that search.'
      : null;

  // ── Desktop: search row + 4-col grid ───────────────────────────────────
  if (isDesktop) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          {search_field}
          <SectionLabel>{countLabel}</SectionLabel>
        </div>
        {emptyMessage && <EmptyHint text={emptyMessage} />}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 14,
          }}
        >
          {filtered.map(renderCard)}
        </div>
      </div>
    );
  }

  // ── Mobile / tablet: single column list ────────────────────────────────
  return (
    <ScreenWrap>
      {search_field}
      <SectionLabel>{countLabel}</SectionLabel>
      {emptyMessage && <EmptyHint text={emptyMessage} />}
      {filtered.map(renderCard)}
    </ScreenWrap>
  );
}

function EmptyHint({ text }) {
  return (
    <div
      style={{
        fontSize: 12.5,
        color: 'rgba(255,255,255,0.45)',
        padding: '14px 4px',
        textAlign: 'center',
        lineHeight: 1.5,
      }}
    >
      {text}
    </div>
  );
}

function DownloadBtn({ attendee, done, onClick, wide }) {
  if (wide) {
    return (
      <button
        onClick={onClick}
        aria-label={`Download contact card for ${attendee.name}`}
        style={{
          width: '100%',
          padding: '8px 12px',
          borderRadius: 9,
          background: done ? '#dcfce7' : ACCENT_LIGHT,
          border: done ? '1px solid #86efac' : `1px solid ${ACCENT_BORDER}`,
          color: done ? '#16a34a' : ACCENT,
          fontFamily: FONT,
          fontSize: 12.5,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          transition: 'all 0.15s',
        }}
      >
        {done ? <Ic.Check size={14} color="#16a34a" /> : <Ic.PersonPlus size={14} color={ACCENT} />}
        {done ? 'Downloaded' : 'Download vCard'}
      </button>
    );
  }
  return (
    <button
      onClick={onClick}
      aria-label={`Download contact card for ${attendee.name}`}
      style={{
        width: 34,
        height: 34,
        borderRadius: 9,
        flexShrink: 0,
        background: done ? '#dcfce7' : ACCENT_LIGHT,
        border: done ? '1px solid #86efac' : `1px solid ${ACCENT_BORDER}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {done ? <Ic.Check size={14} color="#16a34a" /> : <Ic.PersonPlus size={14} color={ACCENT} />}
    </button>
  );
}

// Pretty-print a phone number that might already be formatted, missing dashes,
// or include a country code. Returns the original string for anything we don't
// recognize so we never display worse than what's in the database.
function formatPhone(raw) {
  if (!raw) return '';
  const d = String(raw).replace(/[^\d]/g, '');
  if (d.length === 11 && d.startsWith('1')) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return raw;
}
